import type { ClientPositionView } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";

import { groupHoldings, type Holding, holdingsByPositionUuid } from "@/pages/Positions/holdings";
import type { DistributionItem } from "@/services/clientPositions";

import { positionRewardRate } from "../Positions/positionRewardMetrics";
import { truncateAddress } from "../shared/format";

import { type ChartValue, DAY_MS } from "./chartVisibility";
import { type GraphSlot, graphSlotForIndex } from "./graphColors";

/**
 * Series builders for the three Reporting charts. Pure: API rows in, chart
 * rows out. Token math runs through BigDecimal and converts to `number` only
 * at the edge, because Recharts plots numbers.
 *
 * Every chart row is `{ t, total, [seriesKey]: value }`. Series keys are
 * positional (`s0`, `s1`, ...) so a position uuid can never collide with
 * `t` or `total`.
 */

export type ReportRow = { t: number; total: number | null } & Record<string, number | null>;

export interface ReportSeries {
    key: string;
    label: string;
    slot: GraphSlot;
}

export interface StackedReport {
    series: ReportSeries[];
    rows: ReportRow[];
}

/** Slot for a single portfolio-total series. Olive: the palette's stable,
 *  high-contrast anchor, and never Ember. */
export const TOTAL_SLOT: GraphSlot = "quinary";

function toDay(ms: number): number {
    return Math.floor(ms / DAY_MS) * DAY_MS;
}

/** A report's rows as visibility-rule values: one per x, the row total as y. */
export function reportValues(rows: readonly { t: number; total: number | null }[]): ChartValue[] {
    return rows.map((row) => ({ x: row.t, y: row.total }));
}

/* ── Position naming ────────────────────────────────────── */

interface PositionIdentity {
    key: string;
    name: string;
    address: string;
}

/**
 * Who a payout belongs to: its holding (every Program Position on one wallet
 * or LP position is one series), named the way the Positions page names it.
 * Without a matching position (the list failed, or the payout predates it)
 * the payout's own name stands in.
 */
function identify(item: DistributionItem, holdings: ReadonlyMap<string, Holding>): PositionIdentity {
    const holding = item.positionUuid ? holdings.get(item.positionUuid) : undefined;
    if (holding) {
        return { key: holding.key, name: holding.name, address: holding.address };
    }
    return {
        key: item.positionUuid ?? `account:${item.pool}:${item.sourceWallet}`,
        name: item.positionName ?? item.pool,
        address: item.sourceWallet
    };
}

/** Two positions on one surface share a name; the short address tells them apart. */
function labelSeries(identities: readonly PositionIdentity[]): string[] {
    const counts = new Map<string, number>();
    for (const { name } of identities) counts.set(name, (counts.get(name) ?? 0) + 1);
    return identities.map(({ name, address }) =>
        (counts.get(name) ?? 0) > 1 && address ? `${name} ${truncateAddress(address)}` : name
    );
}

/* ── Earnings (Cumulative), by position ─────────────────── */

/**
 * Cumulative payouts per position on one shared x axis.
 *
 * Payouts are bucketed to their UTC day (one cycle's per-account claims land
 * seconds apart). The x axis is the union of payout days across positions; a
 * position with no payout on a day carries its running total forward, and
 * reads 0 before its first payout, so every row stacks to the portfolio total.
 */
export function buildCumulativeEarnings(
    items: readonly DistributionItem[],
    positions: readonly ClientPositionView[] = []
): StackedReport {
    const byUuid = holdingsByPositionUuid(positions);
    const identities = new Map<string, PositionIdentity & { firstDay: number }>();
    const perDay = new Map<number, Map<string, BigDecimal>>();

    for (const item of items) {
        const ts = Date.parse(item.distributionDate);
        if (!Number.isFinite(ts) || !Number.isFinite(item.amountUsdv)) continue;
        const day = toDay(ts);
        const identity = identify(item, byUuid);
        const known = identities.get(identity.key);
        if (!known || day < known.firstDay) identities.set(identity.key, { ...identity, firstDay: day });

        const bucket = perDay.get(day) ?? new Map<string, BigDecimal>();
        bucket.set(identity.key, (bucket.get(identity.key) ?? new BigDecimal(0)).add(item.amountUsdv));
        perDay.set(day, bucket);
    }

    // Series order: first payout first, so the longest-running position sits
    // at the base of the stack and takes the deepest palette slot.
    const ordered = Array.from(identities.values()).sort(
        (a, b) => a.firstDay - b.firstDay || a.name.localeCompare(b.name)
    );
    const labels = labelSeries(ordered);
    const series: ReportSeries[] = ordered.map((identity, index) => ({
        key: `s${index}`,
        label: labels[index],
        slot: graphSlotForIndex(index)
    }));
    const keyOf = new Map(ordered.map((identity, index) => [identity.key, `s${index}`]));

    const running = new Map<string, BigDecimal>(series.map((s) => [s.key, new BigDecimal(0)]));
    const rows: ReportRow[] = Array.from(perDay.keys())
        .sort((a, b) => a - b)
        .map((day) => {
            for (const [identityKey, amount] of perDay.get(day) ?? []) {
                const key = keyOf.get(identityKey);
                if (!key) continue;
                running.set(key, (running.get(key) ?? new BigDecimal(0)).add(amount));
            }
            let total = new BigDecimal(0);
            const row = { t: day, total: 0 } as ReportRow;
            for (const s of series) {
                const value = running.get(s.key) ?? new BigDecimal(0);
                total = total.add(value);
                row[s.key] = value.toNumber();
            }
            row.total = total.toNumber();
            return row;
        });

    return { series, rows };
}

export interface RateRow {
    t: number;
    total: number | null;
}

export const REWARDS_RATE_HINT =
    "Paid rewards divided by the average qualifying balance for each completed settlement period, annualized. Dates show period ends.";

export const SETTLEMENT_BALANCE_HINT =
    "Average qualifying USDv balance for settlement periods ending on each date. Each holding is counted once.";

/** Reporting uses complete settlement history, independent of daily snapshot collection. */
export function buildSettlementReports(positions: readonly ClientPositionView[]): {
    balances: StackedReport;
    rewardsRate: RateRow[];
} {
    const holdings = groupHoldings(positions);
    const labels = labelSeries(
        holdings.map((holding) => ({ key: holding.key, name: holding.name, address: holding.address }))
    );
    const series = holdings.map((holding, index) => ({
        key: `s${index}`,
        label: labels[index],
        slot: graphSlotForIndex(index)
    }));
    const ends = [
        ...new Set(
            positions.flatMap((position) =>
                (position.rewardMetrics?.periods ?? []).map((period) => Date.parse(period.endsAt))
            )
        )
    ]
        .filter(Number.isFinite)
        .sort((a, b) => a - b);
    const balances: StackedReport = { series, rows: [] };
    const rewardsRate: RateRow[] = [];
    for (const end of ends) {
        const groups = holdings.map((holding) =>
            holding.positions.map((position) => ({
                ...position,
                rewardMetrics: position.rewardMetrics
                    ? {
                          ...position.rewardMetrics,
                          periods: position.rewardMetrics.periods.filter((period) => Date.parse(period.endsAt) === end)
                      }
                    : undefined
            }))
        );
        const row: ReportRow = { t: end, total: null };
        let total = new BigDecimal(0);
        let complete = true;
        for (let i = 0; i < groups.length; i++) {
            const periods = groups[i].flatMap((position) => position.rewardMetrics?.periods ?? []);
            let balance: BigDecimal | null = null;
            for (const period of periods) {
                const value = new BigDecimal(period.averageBalance);
                if (
                    value.isNaN() ||
                    value.lt(0) ||
                    !Number.isFinite(Date.parse(period.startsAt)) ||
                    Date.parse(period.startsAt) >= end
                ) {
                    balance = null;
                    break;
                }
                if (balance === null || value.gt(balance)) balance = value;
            }
            if (balance === null) complete = false;
            else total = total.add(balance);
            row[`s${i}`] = balance?.toNumber() ?? null;
        }
        // A missing holding must not appear as zero or reduce the portfolio total.
        if (complete) row.total = total.toNumber();
        else for (const s of series) row[s.key] = null;
        balances.rows.push(row);
        rewardsRate.push({ t: end, total: complete ? (positionRewardRate(groups, "paid")?.toNumber() ?? null) : null });
    }
    return { balances, rewardsRate };
}
